import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import { searchCustomerRegistrationsByPhone } from 'services/customer-registration.service';
import { withSnackbar } from 'notistack';
import { Controller } from 'react-hook-form';
import { withRouter } from 'react-router-dom';
import { AsYouType } from 'libphonenumber-js';
import { debounce, uniqBy, isEqual } from 'lodash';
import withForm from 'hoc/withForm';
import layoutNames from 'utils/enums/layout-names.enum';
import { can } from 'utils/permissions/permissionsChecker';
import { canByLicensing } from 'utils/licensing/licensingChecker';
import permissionsMap from 'utils/permissions/permissionsMap';
import { setPageHeaderTextActionCreator, setAlertActionCreator } from 'plugins/store/reducers/common.reducer';

import { ratingRequest } from 'services/rating.service';
import { getAllTimezones } from 'services/timeZones.service';
import { initCallPushNotification, initSmsPushNotification } from 'services/push-notification.service';
import { getCustomNotifications, createCustomNotification } from 'services/custom-notifications.service';
import { updateContactCenter, getDetails, getContactCenterImage } from 'services/contact-center.service';
import { DEFAULT_PRE_CALL_NOTIFICATION } from 'utils/constants';

// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Tooltip from '@material-ui/core/Tooltip';
import FormControl from '@material-ui/core/FormControl';

// core components
import Card from 'components/Card/Card';
import GridItem from 'components/Grid/GridItem';
import CardBody from 'components/Card/CardBody';
import NavPills from 'components/NavPills/NavPills';
import AuditLog from 'components/AuditLog/AuditLog';
import Button from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import LogoUpload from 'components/CustomUpload/LogoUpload';
import SnackMessage from 'components/Snackbar/SnackMessage';
import CustomInput from 'components/CustomInput/CustomInput';
import CustomTextField from 'components/CustomTextField/CustomTextField';
import RouteLeavingGuard from 'components/RouteLeavingGuard/RouteLeavingGuard';
import SpoofPreventionList from 'components/SpoofPreventionList/SpoofPreventionList';
import NotificationsPreview from 'components/NotificationsPreview/NotificationsPreview';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import AsyncAutocomplete from 'components/AsyncAutocomplete/AsyncAutocomplete';

import Save from 'assets/img/save.svg';
import EndCall from 'assets/img/EndCall.svg';
import InitCall from 'assets/img/InitCall.svg';
import styles from 'assets/jss/material-dashboard-pro-react/components/contactCentersDetailsStyle';

const timeZones = getAllTimezones();
const MIN_TIME_FOR_RATING_REQUEST_IN_MILLISECONDS = 1 * 1000;

class ContactCentersDetails extends Component {
	state = {
		contactCenter: {
			name: '', //Enterprise Name
			validVerbiage: '', //Default Pre-Call Notification
			verifiedCallArrivalNotification: '', //Verified Call Arrival Notification
			fraudVerbiage: '', //Unverified Call Arrival Notification
			createdBy: '',
			createdAt: '',
			modifiedBy: '',
			orList: [],
			updatedAt: '',
			startOfWork: {
				hour: 0,
				minute: 0,
			},
			endOfWork: {
				hour: 22,
				minute: 0,
			},
			timeZone: 'America/New_York',
		},
		activeTabIndex: 0,
		callFromOptions: [],
		callToProperties: {
			options: [],
			filterQuery: '',
			nextPageIndex: 0,
			pageSize: 15,
			sortBy: [{ id: "name", desc: false }],
			include: ['token'],
			loading: true,
			total: 0,
		},
		customCallPurposeOptions: [],
		allCustomCallPurposes: [],
		callFrom: null,
		callTo: null,
		customCallPurpose: null,
		savedCustomCallPurpose: null,
		customInitSmsMessage: null,
		customEndSmsMessage: null,
		customCallPurposeError: false,
		namePreview: '',
		validVerbiagePreview: '',
		fraudVerbiagePreview: '',
		initCallTooltipShow: false,
		endCallTooltipShow: false,
		defaultVerifiedImage: null,
		defaultVerifiedImageUrl: null,
		verifiedImage: null,
		verifiedImagePreviewUrl: null,
		defaultFraudImage: null,
		defaultFraudImageUrl: null,
		fraudImage: null,
		fraudImagePreviewUrl: null,
		defaultSmsImage: null,
		defaultSmsImageUrl: null,
		smsImage: null,
		smsImagePreviewUrl: null,
		isVerifiedLogoUpdated: false,
		isFraudLogoUpdated: false,
		isSmsLogoUpdated: false,
		calling: false,
		isLoading: true,
		isSettingsLoading: true,
		callStartTime: null,
		initCallNotificationMessageId: null,
		isAllowedSendRatingByLicensing: false,
		isAllowedFraudLogoByLicensing: false,
		isAllowedVerifiedLogoByLicensing: false,
		isAllowedDefaultNotificationByLicensing: false,
		isAllowedCustomNotificationByLicensing: false,
	};

	resolution = { width: 640, height: 960 };
	maxImageSize = 1;
	allowedImageFormats = '.jpg, .jpeg, .png';

	async componentDidMount() {
		await this.refreshDetails();
		this.checkLicensingParams();
		this.props.setPageHeaderText(this.state?.contactCenter?.name);
		this.resetForm();
		this.prepareFromProperties();
		this.prepareCustomCallPurposes();
		this.getLogosUrls();
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	componentDidUpdate(prevProps) {
		if (this.state.isSettingsLoading
			&& (prevProps.organizationName !== this.props.organizationName
				|| prevProps.organizationName !== '')) {

			this.setState({
				isSettingsLoading: false,
			});
		}
	}

	checkLicensingParams = () => {
		const isAllowedSendRatingByLicensing = canByLicensing('rating.rateCall');
		const isAllowedFraudLogoByLicensing = canByLicensing('fraudCalls.customLogos');
		const isAllowedVerifiedLogoByLicensing = canByLicensing('goodCalls.customLogos');
		const isAllowedDefaultNotificationByLicensing = canByLicensing('goodCalls.defaultNotifications');
		const isAllowedCustomNotificationByLicensing = canByLicensing('goodCalls.customNotifications');

		this.setState({
			isAllowedSendRatingByLicensing,
			isAllowedFraudLogoByLicensing,
			isAllowedVerifiedLogoByLicensing,
			isAllowedDefaultNotificationByLicensing,
			isAllowedCustomNotificationByLicensing,
		});

		if (!isAllowedDefaultNotificationByLicensing && !this.state?.contactCenter?.validVerbiage) {
			this.setState((prevState) => ({
				contactCenter: {
					...prevState.contactCenter,
					validVerbiage: DEFAULT_PRE_CALL_NOTIFICATION,
				},
			}));
		}
	}

	refreshDetails = async () => {
		try {
			const id = this.props.match?.params?.id;

			this.setState({
				isLoading: true,
			});
			const result = await getDetails(id);
			const validOrList = result.orList
				.filter((item) => item.phoneNumber);
			this.setState(() => {
				return {
					isLoading: false,
					contactCenter: result,
					callFromOptions: validOrList,
				};
			});
		} catch (error) {
			console.log(error);
		}
	}

	getLogosUrls = async () => {
		const id = this.state?.contactCenter?._id;

		const defaultVerifiedImageData = await getContactCenterImage(id, 'verifiedImage');
		const defaultFraudImageData = await getContactCenterImage(id, 'fraudImage');
		const defaultSmsImageData = await getContactCenterImage(id, 'smsImage');

		this.setState({
			defaultVerifiedImageUrl: URL.createObjectURL(defaultVerifiedImageData),
			defaultFraudImageUrl: URL.createObjectURL(defaultFraudImageData),
			defaultSmsImageUrl: URL.createObjectURL(defaultSmsImageData),
		})
	}

	prepareFromProperties = () => {
		const validOrList = this.state?.contactCenter?.orList
			?.filter((item) => item.phoneNumber) || [];
		this.setState({
			callFromOptions: validOrList,
		});
	};

	prepareCustomCallPurposes = async () => {
		const notifications = await getCustomNotifications();
		const validNotifications =
			uniqBy(
				notifications
					.filter((item) => item.customNotification),
				'customNotification');
		this.setState({
			customCallPurposeOptions: validNotifications,
			allCustomCallPurposes: notifications,
		});
	};

	setCustomCallPurpose = (callTo) => {
		const phoneNumber = callTo?.phoneNumber;
		const customCallPurposes = this.state.allCustomCallPurposes.filter((callPurpose) => {
			return callPurpose.customerPhoneNumbers && callPurpose.customerPhoneNumbers.indexOf(phoneNumber) >= 0;
		});
		const customCallPurpose = phoneNumber
			&& customCallPurposes.length
			&& customCallPurposes[0].customNotification !== ''
			? customCallPurposes[0]
			: null;

		this.setState({
			customCallPurpose,
			savedCustomCallPurpose: customCallPurpose?.customNotification || '',
		});
	};

	confirmSaveCustomCallPurpose = () => {
		const { setAlert } = this.props;
		const warning = {
			type: 'warning',
			title: 'Do you want to save a Custom call purpose in Database',
			message: '',
			confirmBtnText: 'Save',
			cancelBtnText: 'Cancel',
			onConfirm: async () => {
				this.saveCustomCallPurpose();
				setAlert(null);
			},
			onCancel: () => {
				setAlert(null);
			}
		};
		setAlert(warning);
	}

	saveCustomCallPurpose = async (onInitCall) => {
		const { enqueueSnackbar } = this.props;
		try {
			const { phoneNumber } = this.state.callTo;
			const { customNotification = '' } = this.state.customCallPurpose || {};

			const data = { customNotification };
			if (!onInitCall) {
				data.customerPhoneNumber = phoneNumber;
			}

			await createCustomNotification(data);
			this.prepareCustomCallPurposes();

			if (!onInitCall) {
				enqueueSnackbar('Custom Call purpose has been saved', {
					content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
				});
			}
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	resetForm = () => {
		const { reset, trigger } = this.props.formMethods;
		const {
			name,
			validVerbiage,
			fraudVerbiage,
		} = this.state.contactCenter;
		reset({
			name,
			validVerbiage,
			fraudVerbiage,
		});
		trigger();
		this.setState({
			namePreview: name,
			validVerbiagePreview: validVerbiage,
			fraudVerbiagePreview: fraudVerbiage,
		});
	};

	onNavPillsChange = (state) => {
		this.setState({
			activeTabIndex: state.activeTabIndex || 0,
		});
	}

	onBeforeNavPillsChange = async (callBack) => {
		const {
			verifiedImage,
			fraudImage,
			smImage,
			calling,
		} = this.state;
		const {
			formMethods: {
				formState: {
					isDirty,
				},
			},
			setAlert,
		} = this.props;

		if (isDirty || verifiedImage || fraudImage || smImage || calling) {
			const warning = {
				type: 'warning',
				title: 'You have unsaved changes',
				message: 'Are you sure you want to leave the page?',
				confirmBtnText: 'Leave',
				cancelBtnText: 'Stay',
				onConfirm: async () => {
					this.resetForm();
					callBack();
					setAlert(null);
				},
				onCancel: () => {
					setAlert(null);
				},
				...this.getRouteLeavingGuardPropsWhenCall(),
			};
			setAlert(warning);
		} else {
			callBack();
		}
	};

	onVerifiedImageChange = (image, preview) => {
		this.setState({
			verifiedImage: image,
			verifiedImagePreviewUrl: preview,
			isVerifiedLogoUpdated: !!image,
		});
		!image && this.getLogosUrls();
	};

	onFraudImageChange = (image, preview) => {
		this.setState({
			fraudImage: image,
			fraudImagePreviewUrl: preview,
			isFraudLogoUpdated: !!image,
		});
		!image && this.getLogosUrls();
	};

	onSmsImageChange = (image, preview) => {
		this.setState({
			smsImage: image,
			smsImagePreviewUrl: preview,
			isSmsLogoUpdated: !!image,
		});
		!image && this.getLogosUrls();
	};

	onSubmit = async ({
		name = '',
		validVerbiage = '',
		fraudVerbiage = '',
		startOfWork,
		endOfWork,
		timeZone,
	}) => {
		name = name.trim();
		validVerbiage = validVerbiage.trim();
		fraudVerbiage = fraudVerbiage.trim();

		const { currentUserEmail, enqueueSnackbar, history, formMethods: { reset, trigger, formState: { dirtyFields } } } = this.props;
		let updateObject;

		try {
			const { timer } = this.props.notificationTimer;
			const {
				contactCenter: {
					_id,
					orList,
					active,
					createdBy,
				},
				verifiedImage,
				fraudImage,
				smsImage,
			} = this.state;

			updateObject = {
				callCenterId: _id,
				name,
				validVerbiage,
				fraudVerbiage,
				startOfWork,
				endOfWork,
				timeZone,
				modifiedBy: currentUserEmail,
				timer,
				isFraudVerbiageChanged: !!dirtyFields.fraudVerbiage,
				isFraudLogoChanged: !!fraudImage,
				isVerifiedLogoChanged: !!verifiedImage,
				active,
				orList,
				createdBy,
				deleteList: [],
				verifiedImage: verifiedImage,
				fraudImage: fraudImage,
				smsImage: smsImage,
			};

			await updateContactCenter(updateObject);

			reset({
				name,
				validVerbiage,
				fraudVerbiage,
				startOfWork,
				endOfWork,
				timeZone,
			});
			trigger();

			this.setState(({
				contactCenter,
				verifiedImagePreviewUrl,
				fraudImagePreviewUrl,
				smsImagePreviewUrl,
				defaultVerifiedImageUrl,
				defaultFraudImageUrl,
				defaultSmsImageUrl,
			}) => {
				return {
					contactCenter: {
						...contactCenter,
						name,
						validVerbiage,
						fraudVerbiage,
						startOfWork,
						endOfWork,
						timeZone,
					},
					defaultVerifiedImageUrl: verifiedImagePreviewUrl || defaultVerifiedImageUrl,
					defaultFraudImageUrl: fraudImagePreviewUrl || defaultFraudImageUrl,
					defaultSmsImageUrl: smsImagePreviewUrl || defaultSmsImageUrl,
					verifiedImage: null,
					fraudImage: null,
					smsImage: null,
					isVerifiedLogoUpdated: false,
					isFraudLogoUpdated: false,
					isSmsLogoUpdated: false,
				};
			});

			this.props.setPageHeaderText(name);

			history.replace({
				pathname: `/${layoutNames.portal}/contact-centers/details/${_id}`,
				state: {
					...this.state.contactCenter,
					name,
					validVerbiage,
					fraudVerbiage,
					startOfWork,
					endOfWork,
					timeZone,
				},
			});

			enqueueSnackbar('Changes have been saved successfully', {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	debounceNamePreviewUpdate = debounce((value) => {
		this.setState({
			namePreview: value,
		});
	}, 1500);

	debounceValidVerbiagePreviewUpdate = debounce((value) => {
		this.setState({
			validVerbiagePreview: value,
		});
	}, 1500);

	debounceFraudVerbiagePreviewUpdate = debounce((value) => {
		this.setState({
			fraudVerbiagePreview: value,
		});
	}, 1500);

	debounceInitSmsMessagePreviewUpdate = debounce((value) => {
		this.setState({
			customInitSmsMessage: value,
		});
	}, 1500);

	debounceEndSmsMessagePreviewUpdate = debounce((value) => {
		this.setState({
			customEndSmsMessage: value,
		});
	}, 1500);

	onInitCall = async () => {
		const { enqueueSnackbar, currentUserEmail, organizationName } = this.props;
		const {
			name,
			validVerbiage,
			fraudVerbiage,
		} = this.state.contactCenter;
		const {
			isAllowedSendRatingByLicensing,
			savedCustomCallPurpose
		} = this.state;
		const { phoneNumber } = this.state.callFrom;
		const { customNotification = '' } = this.state.customCallPurpose || {};
		const { timer } = this.props.notificationTimer
		const { deviceType, phoneNumber: customerPhoneNumber } = this.state.callTo;
		try {
			const callStartTime = new Date();

			if (customNotification !== savedCustomCallPurpose) {
				await this.saveCustomCallPurpose(true);
			}

			const pushNotification = await initCallPushNotification({
				organizationName,
				name,
				validVerbiage,
				fraudVerbiage,
				createdBy: currentUserEmail,
				modifiedBy: currentUserEmail,
				phoneNumber,
				timer,
				deviceType,
				customerPhoneNumber,
				customCallPurpose: customNotification,
			});

			if (isAllowedSendRatingByLicensing) {
				this.setState({
					initCallNotificationMessageId: pushNotification.messageId,
					callStartTime,
					calling: true,
				});
			} else {
				this.resetCallingParams();
			}

			enqueueSnackbar('Pre-Call Notification has been sent', {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
		} catch (e) {
			this.onEndCall();
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	onEndCall = async () => {
		await this.ratingRequest();
		this.resetCallingParams();
		this.prepareCustomCallPurposes();
	}

	onInitSms = async () => {
		const { enqueueSnackbar } = this.props;
		const smsPayload = {
			contactName: this.state.contactCenter.name,
			sendingNumber: this.state.callFrom.phoneNumber,
			receivingNumber: this.state.callTo.phoneNumber,
			deviceType: this.state.callTo.deviceType,
			validVerbiage: this.state.contactCenter.validVerbiage,
			customInitSmsMessage: this.state.customInitSmsMessage,
			customEndSmsMessage: this.state.customEndSmsMessage,
			createdBy: this.props.currentUserEmail,
			modifiedBy: this.props.currentUserEmail,
			silent: false,
		};
		const response = await initSmsPushNotification(smsPayload);
		enqueueSnackbar('SMS Notification has been sent', {
			content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
		});
	}

	resetCallingParams = () => {
		this.setState({
			calling: false,
			callFrom: null,
			callTo: null,
			customCallPurpose: null,
			callStartTime: null,
			initCallNotificationMessageId: null,
		});
	}

	ratingRequest = async () => {
		if (!can(permissionsMap.rating.items.messageForRequestingRating.create || !this.state.isAllowedSendRatingByLicensing)) {
			return;
		}
		const { enqueueSnackbar } = this.props;
		const callDuration = this.state.callStartTime && new Date() - this.state.callStartTime;

		if (callDuration >= MIN_TIME_FOR_RATING_REQUEST_IN_MILLISECONDS) {
			const data = {
				date: this.state.callStartTime,
				customerNumber: this.state.callTo?.phoneNumber,
				phoneNumber: this.state.callFrom?.phoneNumber,
				callCenterId: this.state.contactCenter?._id,
				initCallNotificationMessageId: this.state.initCallNotificationMessageId,
			};
			try {
				await ratingRequest(data);
				enqueueSnackbar('Rating request has been sent', {
					content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
				});
			} catch (error) {
				enqueueSnackbar(error.resolvedErrorMessage, {
					content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
				});
			}
		}
	}

	handleCallsTooltip = (toolTip, action, isFormDirty) => {
		if (isFormDirty) {
			this.setState({
				[toolTip + 'CallTooltipShow']: action === 'open' ? true : false
			});
		}
	};

	handleUpdateCustomPurpose = (value) => {
		if (value !== '') {
			const newItem = {
				customNotification: value,
			};
			this.setState({
				customCallPurpose: newItem,
			});
		}
		else {
			this.setState({
				customCallPurpose: null,
			});
		}
	};

	onPhoneNumbersListChange = async () => {
		try {
			const id = this.props.match?.params?.id;
			const result = await getDetails(id);
			const validOrList = result.orList.filter((item) => item.phoneNumber);
			this.setState((prevState) => ({
				callFromOptions: validOrList,
				contactCenter: {
					...prevState.contactCenter,
					orList: validOrList,
				},
			}));
		} catch (error) {
			console.log(error);
		}
	}

	getRouteLeavingGuardPropsWhenCall = () => {
		if (this.state.calling) {
			return {
				type: 'info',
				title: 'Notice',
				message: 'You have an Unfinished Call Transaction - please send the Call Ratings to complete',
				confirmBtnText: 'Ok',
				onConfirm: () => {
					this.props.setAlert(null);
				},
			};
		}
		return {};
	}

	debounceInitCall = debounce(() => {
		this.onInitCall();
	}, 500);

	render() {
		const {
			classes,
			formMethods,
			history,
			organizationName,
			contactCenterNameMaxLength,
			defaultNotificationMaxLength,
			unverifiedCallArrivalNotificationMaxLength,
			customNotificationMaxLength,
		} = this.props;

		const { control, trigger, handleSubmit, formState, getValues } = formMethods;

		const { errors, isDirty, isSubmitting } = formState;

		const phoneFilter = createFilterOptions({
			stringify: (option) => `+${option.phoneNumber}`,
		});

		const {
			contactCenter: {
				name, //Enterprise Name
				validVerbiage, //Default Pre-Call Notification
				fraudVerbiage, //Unverified Call Arrival Notification
				createdBy,
				createdAt,
				modifiedBy,
				orList,
				updatedAt,
				startOfWork,
				endOfWork,
				timeZone,
			},
			customInitSmsMessage,
			customEndSmsMessage,
			verifiedImage,
			fraudImage,
			smsImage,
			defaultVerifiedImageUrl,
			verifiedImagePreviewUrl,
			defaultFraudImageUrl,
			fraudImagePreviewUrl,
			defaultSmsImageUrl,
			smsImagePreviewUrl,
			isVerifiedLogoUpdated,
			isFraudLogoUpdated,
			isSmsLogoUpdated,
			calling,
			initCallNotificationMessageId,
			isAllowedSendRatingByLicensing,
			isAllowedFraudLogoByLicensing,
			isAllowedVerifiedLogoByLicensing,
			isAllowedDefaultNotificationByLicensing,
			isAllowedCustomNotificationByLicensing,
		} = this.state;

		return (
			<>
				<GridContainer>
					{
						(this.state.isLoading && this.state.isSettingsLoading)
						&& <div className={classes.loadingIndicator}><LoadingIndicator /></div>
					}
					{(!this.state.isLoading && !this.state.isSettingsLoading) &&
						<GridItem xs={12} sm={12} md={12}>
							<Card className={this.state.activeTabIndex === 0 ? classes.firstTabTopCard : ''}>
								<CardBody>
									<NavPills
										color="warning"
										onNavPillsChange={this.onNavPillsChange}
										onBeforeNavPillsChange={this.onBeforeNavPillsChange}
										tabs={[
											{
												tabButton: "Details",
												tabContent: (
													<form onSubmit={handleSubmit(this.onSubmit)}>
														<GridContainer className={classes.detailsContainer}>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="name"
																	control={control}
																	defaultValue={name}
																	rules={{
																		required: 'Field should not be empty',
																		maxLength: {
																			value: contactCenterNameMaxLength,
																			message: `Maximum ${contactCenterNameMaxLength} characters`
																		}
																	}}
																	render={(props) => (
																		<CustomInput
																			labelText="Contact Center *"
																			error={!!errors.name}
																			helperText={errors.name ? errors.name.message : ""}
																			formControlProps={{
																				fullWidth: true,
																				disabled: !can(permissionsMap.contactCenter.items.contactCenter.update),
																			}}
																			inputProps={{
																				value: props.value,
																				type: "text"
																			}}
																			onChange={(event) => {
																				const value = event.target.value?.trimStart();
																				props.onChange(value);
																				trigger('name');
																				this.debounceNamePreviewUpdate(value);
																			}}
																		/>
																	)}
																/>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="validVerbiage"
																	defaultValue={() => canByLicensing('goodCalls.defaultNotifications') || validVerbiage ? validVerbiage : 'This is a valid call'}
																	control={control}
																	rules={{
																		required: isAllowedDefaultNotificationByLicensing && 'Field should not be empty',
																		maxLength: {
																			value: defaultNotificationMaxLength,
																			message: `Maximum ${defaultNotificationMaxLength} characters`
																		}
																	}}
																	render={(props) => (
																		<CustomInput
																			labelText="Default Pre-Call Notification*"
																			error={!!errors.validVerbiage}
																			helperText={errors.validVerbiage ? errors.validVerbiage.message : ""}
																			formControlProps={{
																				fullWidth: true,
																				disabled: !can(permissionsMap.contactCenter.items.contactCenter.update) || !isAllowedDefaultNotificationByLicensing,
																			}}
																			inputProps={{
																				value: props.value,
																				type: "text"
																			}}
																			onChange={(event) => {
																				const value = event.target.value?.trimStart();
																				props.onChange(value);
																				trigger('validVerbiage');
																				this.debounceValidVerbiagePreviewUpdate(value);
																			}}
																		/>
																	)}
																/>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="fraudVerbiage"
																	defaultValue={fraudVerbiage}
																	control={control}
																	rules={{
																		required: 'Field should not be empty',
																		maxLength: {
																			value: unverifiedCallArrivalNotificationMaxLength,
																			message: `Maximum ${unverifiedCallArrivalNotificationMaxLength} characters`
																		}
																	}}
																	render={(props) => (
																		<CustomInput
																			labelText="Unverified Call Arrival Notification *"
																			error={!!errors.fraudVerbiage}
																			helperText={errors.fraudVerbiage ? errors.fraudVerbiage.message : ""}
																			formControlProps={{
																				fullWidth: true,
																				disabled: !can(permissionsMap.contactCenter.items.contactCenter.update),
																			}}
																			inputProps={{
																				value: props.value,
																				type: "text"
																			}}
																			onChange={(event) => {
																				const value = event.target.value?.trimStart();
																				props.onChange(value);
																				trigger('fraudVerbiage');
																				this.debounceFraudVerbiagePreviewUpdate(value);
																			}}
																		/>
																	)}
																/>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
															</GridItem>
															<GridItem md={12}>
																<h4 className={classes.subTitle}>Work Hours</h4>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="startOfWork"
																	control={control}
																	defaultValue={startOfWork}
																	rules={{
																		required: 'Field should not be empty',
																		validate: () => {
																			const { endOfWork, startOfWork } = getValues();

																			if (isEqual(endOfWork, startOfWork)) {
																				return 'Start time cannot be same as the End time'
																			}
																		}
																	}}
																	render={({ onChange, value }) => (
																		<FormControl fullWidth>
																			<div className={classes.dateTimeWrapper}>
																				<Datetime
																					renderInput={(props) =>
																						<CustomTextField {...props}
																							error={!!errors.startOfWork}
																							helperText={errors.startOfWork ? errors.startOfWork.message : ""}
																							label="Start Time *"
																							margin="normal"
																							inputProps={{
																								readOnly: true,
																								disabled: !can(permissionsMap.contactCenter.items.contactCenter.update),
																							}}
																						/>}
																					value={value}
																					timeConstraints={{
																						minutes: {
																							step: 15
																						},
																					}}
																					dateFormat={false}
																					inputProps={{ fullWidth: true }}
																					onChange={({ _d }) => {
																						const hour = new Date(_d).getHours();
																						const minute = new Date(_d).getMinutes();
																						onChange({ hour, minute });
																						trigger('startOfWork');
																					}}
																				/>
																			</div>
																		</FormControl>
																	)}
																/>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="endOfWork"
																	control={control}
																	defaultValue={endOfWork}
																	rules={{
																		required: 'Field should not be empty',
																		validate: () => {
																			const { endOfWork, startOfWork } = getValues();

																			if (isEqual(endOfWork, startOfWork)) {
																				return 'Start time cannot be same as the End time'
																			}
																		}
																	}}
																	render={({ onChange, value }) => (
																		<FormControl fullWidth>
																			<div className={classes.dateTimeWrapper}>
																				<Datetime
																					renderInput={(props) =>
																						<CustomTextField {...props}
																							error={!!errors.endOfWork}
																							helperText={errors.endOfWork ? errors.endOfWork.message : ""}
																							label="End Time *"
																							margin="normal"
																							inputProps={{
																								readOnly: true,
																								disabled: !can(permissionsMap.contactCenter.items.contactCenter.update),
																							}}
																						/>}
																					value={value}
																					timeConstraints={{
																						minutes: {
																							step: 15
																						},
																					}}
																					dateFormat={false}
																					inputProps={{ fullWidth: true }}
																					onChange={({ _d }) => {
																						const hour = new Date(_d).getHours();
																						const minute = new Date(_d).getMinutes();
																						onChange({ hour, minute });
																						trigger('endOfWork');
																					}}
																				/>
																			</div>
																		</FormControl>
																	)}
																/>
															</GridItem>
															<GridItem xs={12} sm={12} md={6}>
																<Controller
																	name="timeZone"
																	control={control}
																	defaultValue={timeZone}
																	rules={{
																		required: 'Field should not be empty',
																	}}
																	render={({ onChange, value }) => (
																		<Autocomplete
																			disabled={!can(permissionsMap.contactCenter.items.contactCenter.update)}
																			options={timeZones}
																			autoComplete
																			includeInputInList
																			value={value}
																			filterOptions={createFilterOptions({ trim: true })}
																			renderInput={(params) =>
																				<CustomTextField {...params}
																					error={!!errors.timeZone}
																					helperText={errors.timeZone ? errors.timeZone.message : ""}
																					label="Time Zone *"
																					margin="normal" />
																			}
																			onChange={(e, val) => {
																				onChange(val);
																				trigger('timeZone');
																			}}
																			onBlur={() => {
																				trigger('timeZone');
																			}}
																		/>
																	)}
																/>
															</GridItem>
															<GridItem md={6} />
															{
																can(permissionsMap.contactCenter.items.skinsUpload.update)
																&& (
																	<GridItem xs={12} sm={12} md={6}>
																		<LogoUpload
																			disabled={!isAllowedVerifiedLogoByLicensing}
																			label="Upload Verified Call Image"
																			allowedFormats={this.allowedImageFormats}
																			maxSize={this.maxImageSize}
																			resolution={this.resolution}
																			onImageChange={this.onVerifiedImageChange}
																			isUnsaved={isVerifiedLogoUpdated}
																		/>
																	</GridItem>
																)
															}
															{
																can(permissionsMap.contactCenter.items.skinsUpload.update)
																&& (
																	<GridItem xs={12} sm={12} md={6}>
																		<LogoUpload
																			disabled={!isAllowedFraudLogoByLicensing}
																			label="Upload Fraud Call Image"
																			allowedFormats={this.allowedImageFormats}
																			maxSize={this.maxImageSize}
																			resolution={this.resolution}
																			onImageChange={this.onFraudImageChange}
																			isUnsaved={isFraudLogoUpdated}
																		/>
																	</GridItem>
																)
															}
															{
                                                                                                                                can(permissionsMap.contactCenter.items.skinsUpload.update)
                                                                                                                                && (
                                                                                                                                        <GridItem xs={12} sm={12} md={6}>
                                                                                                                                                <LogoUpload
                                                                                                                                                        disabled={!isAllowedVerifiedLogoByLicensing}
                                                                                                                                                        label="Upload Verified SMS Image"
                                                                                                                                                        allowedFormats={this.allowedImageFormats}
                                                                                                                                                        skipFileValidation="true"
                                                                                                                                                        maxSize={this.maxImageSize}
                                                                                                                                                        resolution={this.resolution}
                                                                                                                                                        onImageChange={this.onSmsImageChange}
                                                                                                                                                        isUnsaved={isSmsLogoUpdated}
                                                                                                                                                />
                                                                                                                                        </GridItem>
                                                                                                                                )
                                                                                                                        }
															<GridItem xs={12} sm={12} md={12} className={classes.actionContainer}>
																<Button
																	color='success'
																	size='sm'
																	type='submit'
																	disabled={
																		!!Object.keys(errors).length
																		|| isSubmitting
																		|| (!isDirty && !isVerifiedLogoUpdated && !isFraudLogoUpdated && !isSmsLogoUpdated)
																	}
																>Save</Button>
															</GridItem>
														</GridContainer>
														<RouteLeavingGuard
															alertCustomProps={this.getRouteLeavingGuardPropsWhenCall()}
															when={isDirty || verifiedImage || fraudImage || calling}
															navigate={path => history.push(path)}
															shouldBlockNavigation={({ pathname }) => {
																if (pathname === history.location.pathname) {
																	return false;
																}
																if (isDirty || verifiedImage || fraudImage || calling) {
																	return true;
																}
																return false
															}}
														/>
													</form>
												)
											},
											can(permissionsMap.contactCenter.items.spoofPreventionList.read) &&
											{
												tabButton: "Spoof Prevention List",
												tabContent: (
													<SpoofPreventionList
														parentContactCenter={this.state.contactCenter}
														data={orList}
														onPhoneNumbersListChange={this.onPhoneNumbersListChange}
													/>
												)
											},
											{
												tabButton: "Audit Log",
												tabContent: (
													<AuditLog
														createdBy={createdBy}
														createdAt={createdAt}
														modifiedBy={modifiedBy}
														modifiedAt={updatedAt}
													/>
												)
											}
										].filter(Boolean)}
									/>
								</CardBody>
							</Card>

							{this.state.activeTabIndex === 0 &&
								(!this.state.isLoading && !this.state.isSettingsLoading) &&
								<>
									<Card className={classes.firstTabBottomCard}>
										<CardBody>
											<GridContainer className={classes.detailsPreCallContainer}>
												<GridItem xs={12} sm={12} md={6}>
													<Autocomplete
														options={this.state.callFromOptions}
														autoComplete
														includeInputInList
														disabled={calling}
														value={this.state.callFrom}
														getOptionLabel={(option) => {
															const phoneNumber = new AsYouType('US').input(option.phoneNumber);
															return `+${phoneNumber}`;
														}}
														filterOptions={(options, params) => {
															return phoneFilter(options, params);
														}}
														renderInput={(params) =>
															<CustomTextField {...params}
																label="Phone Number Calling from *"
																margin="normal"
															/>}
														onChange={(e, val) => {
															this.setState({ callFrom: val });
														}}
													/>
												</GridItem>
											</GridContainer>
											<GridContainer className={classes.detailsContainer}>
												<GridItem xs={12} sm={12} md={6}>
													<AsyncAutocomplete
														label="Select Customer Call to *"
														optionLabel="phoneNumber"
														isPhoneSearch={true}
														value={this.state.callTo}
														searchHandler={searchCustomerRegistrationsByPhone}
														searchOptions={{
															sortBy: [{ id: "phoneNumber", desc: false }],
															include: ['phoneNumber'],
														}}
														filterOptions={(options, params) => {
															return phoneFilter(options, params);
														}}
														disabled={calling}
														onChange={(val) => {
															this.setState({ callTo: val });
															this.setCustomCallPurpose(val);
														}}
													/>
												</GridItem>
												<GridItem xs={12} sm={12} md={6} className={classes.customCallContainer}>
													<Autocomplete
														options={this.state.customCallPurposeOptions}
														className={classes.customCallPurpose}
														autoComplete
														freeSolo
														disabled={calling || !isAllowedCustomNotificationByLicensing}
														value={this.state.customCallPurpose}
														includeInputInList
														renderInput={(params) => <CustomTextField {...params}
															label="Custom Call Purpose"
															margin="normal"
															error={this.state.customCallPurposeError}
															helperText={
																this.state.customCallPurposeError
																	? `Maximum ${customNotificationMaxLength} characters`
																	: ''
															}
															onChange={(event) => {
																const isError = customNotificationMaxLength < event.target.value.length;
																if (isError !== this.state.customCallPurposeError) {
																	this.setState({ customCallPurposeError: isError });
																}
															}}
														/>}
														renderOption={(option) => option.customNotification}
														onChange={(e, value) => {
															if (value && value.inputValue) {
																// Create a new value from the user input
																const inputValue = value.inputValue.trim();
																this.handleUpdateCustomPurpose(inputValue);
															} else {
																this.setState({
																	customCallPurpose: value,
																	customCallPurposeError: false,
																});
															}
														}}
														onBlur={(event) => {
															const value = event.target.value.trim();
															this.handleUpdateCustomPurpose(value);
														}}
														getOptionLabel={(option) => {
															// Value selected with enter, right from the input
															if (typeof option === 'string') {
																return option;
															}
															// Add "xxx" option created dynamically
															if (option.inputValue) {
																return option.inputValue;
															}
															// Regular option
															return option.customNotification;
														}}
													/>
													<Tooltip
														arrow
														title={
															(!this.state.callTo
																|| this.state.customCallPurposeError
																|| calling
																|| !isAllowedCustomNotificationByLicensing) ? 'Please, select a Customer Call to number' : ''
														}
													>
														<span className={classes.customCallSaveWrapper}>
															<Button
																justIcon
																simple
																className={classes.customCallSave}
																disabled={
																	!this.state.callTo
																	|| this.state.customCallPurposeError
																	|| calling
																	|| !isAllowedCustomNotificationByLicensing
																}
																onClick={() => this.confirmSaveCustomCallPurpose()}>
																<img
																	src={Save}
																	alt='Save'
																/>
															</Button>
														</span>
													</Tooltip>
												</GridItem>
												<GridItem md={12}>
													<h4 className={classes.subTitle}>SMS Messages</h4>
												</GridItem>
												<GridItem xs={12} sm={12} md={6}>
													<Controller
														name="customInitSmsMessage"
														defaultValue={() => customInitSmsMessage ? customInitSmsMessage : ""}
														control={control}
														rules={{
															required: false,
														}}
														render={(props) => (
															<CustomInput
																labelText="Custom Pre-SMS Message"
																error={!!errors.customInitSmsMessage}
																helperText={errors.customInitSmsMessage ? errors.customInitSmsMessage.message : ""}
																formControlProps={{
																	fullWidth: true,
																	disabled: false,
																}}
																inputProps={{
																	value: props.value,
																	type: "text"
																}}
																onChange={(event) => {
																	const value = event.target.value?.trimStart();
																	props.onChange(value);
																	trigger('customInitSmsMessage');
																	this.debounceInitSmsMessagePreviewUpdate(value);
																}}
															/>
														)}
													/>
												</GridItem>
												<GridItem xs={12} sm={12} md={6}>
													<Controller
														name="customEndSmsMessage"
														defaultValue={() => customEndSmsMessage ? customEndSmsMessage : ""}
														control={control}
														rules={{
															required: false,
														}}
														render={(props) => (
															<CustomInput
																labelText="Custom Post-SMS Message"
																error={!!errors.customEndSmsMessage}
																helperText={errors.customEndSmsMessage ? errors.customEndSmsMessage.message : ""}
																formControlProps={{
																	fullWidth: true,
																	disabled: false,
																}}
																inputProps={{
																	value: props.value,
																	type: "text"
																}}
																onChange={(event) => {
																	const value = event.target.value?.trimStart();
																	props.onChange(value);
																	trigger('customEndSmsMessage');
																	this.debounceEndSmsMessagePreviewUpdate(value);
																}}
															/>
														)}
													/>
												</GridItem>
												<GridItem xs={12} sm={12} md={6}>
													<br />
													<legend>Preview:</legend>
												</GridItem>
											</GridContainer>
											<NotificationsPreview
												organizationName={organizationName}
												enterpriseName={this.state.namePreview}
												phoneNumber={this.state.callFrom ? this.state.callFrom.phoneNumber : ''}
												notification={this.state.customCallPurpose ? this.state.customCallPurpose.customNotification : this.state.validVerbiagePreview}
												unverifiedCall={this.state.fraudVerbiagePreview}
												defaultVerifiedImage={defaultVerifiedImageUrl}
												verifiedImage={verifiedImagePreviewUrl}
												defaultFraudImage={defaultFraudImageUrl}
												fraudImage={fraudImagePreviewUrl}
												defaultSmsImage={defaultSmsImageUrl}
												smsImage={smsImagePreviewUrl}
											/>
										</CardBody>
									</Card>
								</>
							}
						</GridItem>
					}
				</GridContainer>
				{
					this.state.activeTabIndex === 0 &&
					!this.state.isLoading &&
					<div>
						<div className={classes.phantomFooter} />
						<div className={classes.footer}>
							<Tooltip
                                                                arrow
                                                                open={this.state.initCallTooltipShow}
                                                                onOpen={() => this.handleCallsTooltip('init', 'open', isDirty)}
                                                                onClose={() => this.handleCallsTooltip('init', 'close', isDirty)}
                                                                title={"Please save your changes before sending SMS notification"} >
                                                                <span>
                                                                        <Button
                                                                                color='success'
                                                                                size='sm'
                                                                                type='button'
                                                                                className={classes.preCallButtons}
                                                                                onClick={() => this.onInitSms()}
                                                                                disabled={
                                                                                        !this.state.callFrom
                                                                                        || !this.state.callTo
                                                                                        || isDirty
                                                                                }
                                                                        ><img
                                                                                        className={classes.buttonIcon}
                                                                                        src={InitCall}
                                                                                        alt='InitCall'
                                                                                />Pre-SMS Notification</Button>
                                                                </span>
                                                        </Tooltip>
							<Tooltip
								arrow
								open={this.state.initCallTooltipShow}
								onOpen={() => this.handleCallsTooltip('init', 'open', isDirty)}
								onClose={() => this.handleCallsTooltip('init', 'close', isDirty)}
								title={"Please, save your changes before sending Pre-Call Notification"} >
								<span>
									<Button
										color='success'
										size='sm'
										type='button'
										className={classes.preCallButtons}
										onClick={() => this.debounceInitCall()}
										disabled={
											!this.state.callFrom
											|| !this.state.callTo
											|| isDirty
											|| calling
											|| this.state.customCallPurposeError
										}
									><img
											className={classes.buttonIcon}
											src={InitCall}
											alt='InitCall'
										/>Pre-Call Notification</Button>
								</span>
							</Tooltip>
							{isAllowedSendRatingByLicensing &&
								<Tooltip
									arrow
									open={this.state.endCallTooltipShow}
									onOpen={() => this.handleCallsTooltip('end', 'open', isDirty)}
									onClose={() => this.handleCallsTooltip('end', 'close', isDirty)}
									title={"Please, save your changes before sending Rating request"} >
									<span>
										<Button
											color='danger'
											size='sm'
											type='button'
											className={classes.preCallButtons}
											onClick={() => this.onEndCall()}
											disabled={!this.state.callFrom || !this.state.callTo || isDirty || !calling || !initCallNotificationMessageId}
										><img
												className={classes.buttonIcon}
												src={EndCall}
												alt='EndCall'
											/><span>Rating request</span></Button>
									</span>
								</Tooltip>
							}
						</div>
					</div>
				}
			</>
		)
	}
}

ContactCentersDetails.propTypes = {
	classes: PropTypes.any,
	setPageHeaderText: PropTypes.func,
	location: PropTypes.object,
	match: PropTypes.any,
	formMethods: PropTypes.any,
	setAlert: PropTypes.any,
};

const mapStateToProps = ({ auth, settings }) => {
	return {
		currentUserEmail: auth.auth?.email,
		organizationName: settings.organizationName,
		notificationTimer: {
			timer: settings.pushNotificationTimer,
			endCallNotification: settings.defaultEndCallNotification,
		},
		contactCenterNameMaxLength: settings.contactCenterNameMaxLength,
		defaultNotificationMaxLength: settings.defaultNotificationMaxLength,
		unverifiedCallArrivalNotificationMaxLength: settings.unverifiedCallArrivalNotificationMaxLength,
		customNotificationMaxLength: settings.customNotificationMaxLength,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setPageHeaderText: bindActionCreators(setPageHeaderTextActionCreator, dispatch),
});

export default compose(
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps),
	withSnackbar,
	withForm,
	withRouter
)(ContactCentersDetails);
